import type {
  ConnectionsResponse,
  CreateCrosspostRequest,
  CreateFacebookPostRequest,
  CreateInstagramPostRequest,
  CreatePortalLinkResponse,
  CrosspostingStatusResponse,
  CrosspostMultiResponse,
  CrosspostPostResponse,
  ListCrosspostPostsResponse,
  SocialPlatform,
} from '@pulsify/shared/dto'
import { adminBypassHeaders } from '~/app/admin-bypass'
import { api, getAuthToken, unwrap } from '~/shared/lib/rpc'

export const crosspostingApi = {
  async getStatus(businessId: string): Promise<CrosspostingStatusResponse> {
    const res = await api.api.businesses[':businessId'].crossposting.status.$get({
      param: { businessId },
    })
    return unwrap<CrosspostingStatusResponse>(res)
  },

  async getConnections(businessId: string, listingId: string): Promise<ConnectionsResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[':listingId'].connections.$get(
      {
        param: { businessId, listingId },
      },
    )
    return unwrap<ConnectionsResponse>(res)
  },

  async refresh(businessId: string, listingId: string): Promise<ConnectionsResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[
      ':listingId'
    ].connections.refresh.$post({
      param: { businessId, listingId },
    })
    return unwrap<ConnectionsResponse>(res)
  },

  async createPortalLink(
    businessId: string,
    listingId: string,
    platform: SocialPlatform,
  ): Promise<CreatePortalLinkResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[':listingId'][
      'portal-link'
    ].$post({
      param: { businessId, listingId },
      json: { platform },
    })
    return unwrap<CreatePortalLinkResponse>(res)
  },

  async disconnect(
    businessId: string,
    listingId: string,
    platform: SocialPlatform,
  ): Promise<ConnectionsResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[':listingId'].disconnect.$post(
      {
        param: { businessId, listingId },
        json: { platform },
      },
    )
    return unwrap<ConnectionsResponse>(res)
  },

  async createFacebookPost(
    businessId: string,
    listingId: string,
    payload: CreateFacebookPostRequest,
    files: File[],
  ): Promise<CrosspostPostResponse> {
    const url = api.api.businesses[':businessId'].crossposting[':listingId'].facebook.post.$url({
      param: { businessId, listingId },
    })
    const form = new FormData()
    form.append('payload', JSON.stringify(payload))
    for (const file of files) form.append('files', file)

    const token = await getAuthToken()
    const headers: Record<string, string> = { ...adminBypassHeaders() }
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, { method: 'POST', body: form, headers })
    return unwrap<CrosspostPostResponse>(res)
  },

  async createInstagramPost(
    businessId: string,
    listingId: string,
    payload: CreateInstagramPostRequest,
    files: File[],
  ): Promise<CrosspostPostResponse> {
    const url = api.api.businesses[':businessId'].crossposting[':listingId'].instagram.post.$url({
      param: { businessId, listingId },
    })
    const form = new FormData()
    form.append('payload', JSON.stringify(payload))
    for (const file of files) form.append('files', file)

    const token = await getAuthToken()
    const headers: Record<string, string> = { ...adminBypassHeaders() }
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, { method: 'POST', body: form, headers })
    return unwrap<CrosspostPostResponse>(res)
  },

  async createCrosspost(
    businessId: string,
    listingId: string,
    payload: CreateCrosspostRequest,
    files: File[],
  ): Promise<CrosspostMultiResponse> {
    const url = api.api.businesses[':businessId'].crossposting[':listingId']['cross-post'].$url({
      param: { businessId, listingId },
    })
    const form = new FormData()
    form.append('payload', JSON.stringify(payload))
    for (const file of files) form.append('files', file)

    const token = await getAuthToken()
    const headers: Record<string, string> = { ...adminBypassHeaders() }
    if (token) headers.Authorization = `Bearer ${token}`

    const res = await fetch(url, { method: 'POST', body: form, headers })
    return unwrap<CrosspostMultiResponse>(res)
  },

  async listPosts(
    businessId: string,
    listingId: string,
    limit: number,
    offset: number,
  ): Promise<ListCrosspostPostsResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[':listingId'].posts.$get({
      param: { businessId, listingId },
      query: { limit: String(limit), offset: String(offset) },
    })
    return unwrap<ListCrosspostPostsResponse>(res)
  },

  async refreshPosts(
    businessId: string,
    listingId: string,
    limit: number,
  ): Promise<ListCrosspostPostsResponse> {
    const res = await api.api.businesses[':businessId'].crossposting[
      ':listingId'
    ].posts.refresh.$post({
      param: { businessId, listingId },
      query: { limit: String(limit), offset: '0' },
    })
    return unwrap<ListCrosspostPostsResponse>(res)
  },

  async deletePost(businessId: string, listingId: string, postId: string): Promise<void> {
    const res = await api.api.businesses[':businessId'].crossposting[':listingId'].posts[
      ':postId'
    ].$delete({
      param: { businessId, listingId, postId },
    })
    await unwrap<void>(res)
  },
}
